import { Target, Users, Zap, Award, Shield, Rocket, CheckCircle2, TrendingUp } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

const features = [
  {
    icon: Target,
    title: 'ROI-Obsessed Approach',
    description: 'Every line of code we write is designed to drive measurable business outcomes. Our average client sees 385% ROI within 12 months—and we guarantee results.',
  },
  {
    icon: Users,
    title: 'Elite Engineering Teams',
    description: 'Work with senior engineers averaging 12+ years of experience. No juniors, no offshore handoffs—just world-class talent dedicated to your success.',
  },
  {
    icon: Zap,
    title: 'Ruthlessly Fast Execution',
    description: 'We move at startup speed with enterprise quality. Agile sprints, daily updates, and continuous delivery mean you see progress every week, not every quarter.',
  },
  {
    icon: Shield,
    title: 'Enterprise-Grade Security',
    description: 'SOC 2, HIPAA, GDPR, PCI DSS—we build security into every layer. Your data and your customers\' trust are never compromised.',
  },
];

const stats = [
  { value: '500+', label: 'Projects Delivered', icon: Rocket },
  { value: '$2.5B+', label: 'Client Value Created', icon: TrendingUp },
  { value: '99%', label: 'Client Retention', icon: Award },
];

export function WhyChooseUs() {
  return (
    <section id="about" className="py-20 md:py-32 relative overflow-hidden">
      {/* Premium background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20" />
      <div className="absolute top-1/2 right-0 w-[700px] h-[700px] bg-gradient-to-br from-purple-600/5 to-transparent rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Enhanced Image Section */}
          <div className="relative order-2 lg:order-1 animate-fadeInUp">
            <div className="relative rounded-3xl overflow-hidden shadow-premium-lg border border-gray-200">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-500/5 to-transparent z-10" />
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1531498860502-7c67cf02f657?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwY29kZXxlbnwxfHx8fDE3Njc2NzY1OTB8MA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Elite software development team"
                className="w-full h-auto"
              />
            </div>

            {/* Enhanced Floating Cards */}
            <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-premium-lg p-8 border border-gray-100 max-w-xs animate-float" style={{ animationDelay: '1s' }}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                  <TrendingUp size={28} className="text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-600 font-semibold">Average Client ROI</div>
                  <div className="text-3xl font-extrabold gradient-text-primary">385%</div>
                </div>
              </div>
              <p className="text-xs text-gray-600">Measured over 12 months across all engagements</p>
            </div>

            <div className="hidden lg:block absolute -top-8 -left-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-premium-lg p-6 text-white max-w-[200px] animate-float">
              <div className="text-4xl font-extrabold mb-2">24/7</div>
              <div className="text-sm font-medium">Expert support when you need it</div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="order-1 lg:order-2 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-600/10 via-purple-500/10 to-cyan-500/10 border border-blue-200/50 mb-6">
              <Award size={16} className="text-blue-600" />
              <span className="text-sm text-blue-700 font-semibold uppercase tracking-wider">
                Why ZeeJay Labs
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              <span className="gradient-text-premium">We Don't Just Build.</span>
              <br />
              <span className="text-gray-900">We Transform.</span>
            </h2>
            
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
              Other firms deliver code. We deliver competitive advantages. Our battle-tested methodology combines strategic thinking, elite engineering, and relentless execution to create solutions that actually move the needle.
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-6 mb-10 pb-10 border-b-2 border-gray-200">
              {stats.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="text-center lg:text-left">
                    <Icon size={24} className="text-blue-600 mb-2 mx-auto lg:mx-0" />
                    <div className="text-2xl md:text-3xl font-extrabold gradient-text-primary mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">
                      {stat.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Enhanced Features */}
            <div className="space-y-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="group flex gap-4 p-4 rounded-xl hover:bg-gray-50/50 transition-colors"
                  >
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg">
                      <Icon size={24} />
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold mb-2 text-gray-900">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-100">
              <div className="flex items-start gap-4">
                <CheckCircle2 size={24} className="text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Risk-Free Guarantee</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    We're so confident in our ability to deliver results that we offer a satisfaction guarantee. If we don't meet your objectives in the first 90 days, we'll work for free until we do.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}